import React, { Component, type SyntheticEvent } from 'react';
import flow from 'lodash/flow';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import { copyToClipboard } from '@atlaskit/editor-common/clipboard';
import AkEmailIcon from '@atlaskit/icon/core/email';
import CopyIcon from '@atlaskit/icon/core/migration/copy';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import withFireUiAnalytics from '@atlassian/jira-analytics-web-react/src/components/with-fire-ui-analytics.tsx';
import { SERVICE_DESK_PROJECT } from '@atlassian/jira-common-constants/src/project-types.tsx';
import { type IntlShape, injectIntl } from '@atlassian/jira-intl/src/index.tsx';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
import { getNextgenSettingsUrl } from '@atlassian/jira-project-settings-apps-common/src/urls.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { ChannelStatusLozenge, LOZENGE_TYPES } from '../../status-lozenge/index.tsx';
import { BaseChannelItem, ChannelItemImage } from '../index.tsx';
import EmailIcon from './assets/index.tsx';
import messages from './messages.tsx';

type Props = {
	isEnabled: boolean;
	isAdmin: boolean;
	isProjectSimplified: boolean;
	email: string;
	projectKey: string;
	onClick: () => void;
	onCopyClick: (event: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => void;
	intl: IntlShape;
};

// eslint-disable-next-line jira/react/no-class-components
class EmailChannelViewComponent extends Component<Props> {
	static defaultProps = {
		email: '',
	};

	getClassicConfigurationLink() {
		const { projectKey } = this.props;
		return `/jira/servicedesk/projects/${projectKey}/settings/email`;
	}

	getConfigurationLink() {
		const { projectKey, isProjectSimplified } = this.props;
		return isProjectSimplified
			? `${getNextgenSettingsUrl(SERVICE_DESK_PROJECT, projectKey)}/email`
			: this.getClassicConfigurationLink();
	}

	image = (
		<ChannelItemImage>
			<EmailIcon />
		</ChannelItemImage>
	);

	render() {
		const {
			isEnabled,
			isAdmin,
			email,
			onClick,
			intl: { formatMessage },
		} = this.props;

		const onCopyClick = (event: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
			copyToClipboard(email);
			this.props.onCopyClick(event, analyticsEvent);
		};

		const configureEmailButton = isAdmin ? (
			<Button
				href={this.getConfigurationLink()}
				onClick={onClick}
				appearance="default"
				{...(fg('jsm_aiiy_for_nav_item_channel')
					? { 'aria-label': formatMessage(messages.configureLabelDescriptive) }
					: {})}
			>
				{formatMessage(messages.configure)}
			</Button>
		) : undefined;

		const copyEmailButton = (
			<Tooltip delay={100} content={formatMessage(messages.copyEmail)}>
				<Button
					onClick={onCopyClick}
					iconAfter={<CopyIcon label="" LEGACY_size="medium" spacing="spacious" />}
					appearance="default"
					aria-label={formatMessage(messages.copyEmail)}
				/>
			</Tooltip>
		);

		const emailButton = !isAdmin && isEnabled ? copyEmailButton : configureEmailButton;

		const lozengeType = isEnabled ? LOZENGE_TYPES.ON : LOZENGE_TYPES.OFF;
		const lozengeLabel = formatMessage(
			isEnabled ? messages.emailEnabledLabel : messages.emailOffLabel,
		);
		const isNav4 = getWillShowNav4();

		const description = isEnabled
			? email
			: formatMessage(isNav4 ? messages.emailOff : messages.channelTurnedOff);

		const icon = isNav4 ? <AkEmailIcon color={token('color.icon')} label="" /> : this.image;

		const label = isNav4 ? formatMessage(messages.email) : formatMessage(messages.emailLabel);

		return (
			<BaseChannelItem
				button={emailButton}
				image={icon}
				label={label}
				description={description}
				channelName="email"
				statusLozenge={!isNav4 && <ChannelStatusLozenge type={lozengeType} label={lozengeLabel} />}
			/>
		);
	}
}

export const EmailChannelView = flow(
	withFireUiAnalytics({
		onClick: 'configureEmail',
		onCopyClick: 'copyEmail',
	}),
	injectIntl,
)(EmailChannelViewComponent);
